/**
 * Centralized API client for DomainKings frontend.
 *
 * Single source of truth for:
 *  - Base URL (REACT_APP_API_BASE_URL)
 *  - Default headers
 *  - Error handling
 *  - Auth headers (admin key when available)
 */

const API_BASE = (
  process.env.REACT_APP_API_BASE_URL || ''
).replace(/\/+$/, '');

function buildApiUrl(path) {
  const safePath = String(path || '');
  if (!API_BASE) return safePath;

  // Prevent duplicated "/api" when env base already includes it and callers pass "/api/...".
  if (API_BASE.endsWith('/api') && safePath.startsWith('/api/')) {
    return `${API_BASE}${safePath.slice(4)}`;
  }

  return `${API_BASE}${safePath}`;
}

/**
 * Normalize FastAPI error payloads (detail string, validation array, or plain message).
 */
function extractApiErrorMessage(data, status, rawText) {
  if (!data && rawText && typeof rawText === 'string') {
    const t = rawText.trim();
    if (t.length > 0 && t.length < 500) return t;
    return `Request failed (${status})`;
  }
  if (!data) return `Request failed (${status})`;
  const d = data.detail;
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) {
    return d
      .map((x) => (x && typeof x === 'object' && x.msg ? x.msg : JSON.stringify(x)))
      .join('; ');
  }
  if (d && typeof d === 'object' && typeof d.msg === 'string') return d.msg;
  return data.error || data.message || `Request failed (${status})`;
}

/**
 * Make an API request with consistent error handling.
 *
 * @param {string} path  - API path (e.g. "/api/orders/create")
 * @param {object} [options] - fetch options (method, body, headers, etc.)
 * @returns {Promise<any>} parsed JSON response
 * @throws {Error} with message from server or generic network error
 */
export async function apiFetch(path, options = {}) {
  const url = buildApiUrl(path);

  const headers = {
    ...options.headers,
  };

  // Auto-set Content-Type for JSON bodies
  if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const resp = await fetch(url, { ...options, headers });

  let data;
  let rawText = '';
  try {
    rawText = await resp.text();
    data = rawText ? JSON.parse(rawText) : null;
  } catch (_e) {
    data = null;
  }

  if (!resp.ok) {
    const msg = extractApiErrorMessage(data, resp.status, rawText);
    const err = new Error(msg);
    err.status = resp.status;
    err.data = data;
    throw err;
  }

  return data;
}

/**
 * GET helper.
 * @param {string} path
 * @param {object} [params] - query string params (key/value)
 */
export function apiGet(path, params) {
  let url = path;
  if (params) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.set(k, v);
    });
    const qsStr = qs.toString();
    if (qsStr) url += `?${qsStr}`;
  }
  return apiFetch(url);
}

/**
 * POST helper (JSON body).
 * @param {string} path
 * @param {object} body
 */
export function apiPost(path, body) {
  return apiFetch(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export { API_BASE };
export default apiFetch;
